
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Send, CheckCircle2, AlertCircle } from 'lucide-react';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation();
  const [category, setCategory] = useState('comment');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      setTimeout(() => {
        setStatus('idle');
        setMessage('');
        setCategory('comment');
      }, 300);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    setStatus('sending');

    const metadata = {
      url: window.location.href,
      language: i18n.language,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    };

    try {
      const WORKER_URL = 'https://home.rtneg.workers.dev/'; 
      
      const response = await fetch(WORKER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category,
          message,
          metadata
        }),
      });

      if (response.ok) {
        setStatus('success');
      } else {
        throw new Error('Failed to send');
      }
    } catch (err) {
      console.error('Feedback submission error:', err);
      setStatus('error');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 animate-fade-in">
      <div 
        className="absolute inset-0 bg-[#0F172A]/40 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      
      <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden transform transition-all">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h3 className="text-xl font-bold text-[#0F172A]">{t('feedback.title')}</h3>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="p-6">
          {status === 'success' ? (
            <div className="py-8 text-center space-y-4 animate-fade-in">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-50 text-green-500 rounded-full mb-2">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h4 className="text-2xl font-bold text-[#0F172A]">{t('feedback.success_title')}</h4>
              <p className="text-gray-500">{t('feedback.success_msg')}</p>
              <button
                onClick={onClose}
                className="mt-6 px-8 py-3 bg-[#0F172A] text-white rounded-xl font-medium hover:bg-[#1E293B] transition-all"
              >
                {t('feedback.close')}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <p className="text-sm text-gray-500">{t('feedback.description')}</p>
              
              <div className="space-y-2">
                <label className="text-sm font-semibold text-[#0F172A]">{t('feedback.category')}</label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {[
                    { id: 'bug', label: t('feedback.cat_bug'), color: 'text-red-500 bg-red-50 border-red-100' },
                    { id: 'request', label: t('feedback.cat_request'), color: 'text-blue-500 bg-blue-50 border-blue-100' },
                    { id: 'comment', label: t('feedback.cat_comment'), color: 'text-indigo-500 bg-indigo-50 border-indigo-100' }
                  ].map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setCategory(cat.id)}
                      className={`px-3 py-2 text-xs font-medium rounded-lg border transition-all ${
                        category === cat.id 
                        ? `${cat.color} ring-2 ring-offset-1 ring-indigo-500/20` 
                        : 'bg-white text-gray-400 border-gray-100 hover:border-gray-300'
                      }`}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-[#0F172A]">{t('feedback.message_label')}</label>
                <textarea
                  required
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t('feedback.message_placeholder')}
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-2xl focus:bg-white focus:border-[#3B82F6] focus:ring-4 focus:ring-blue-500/5 transition-all outline-none resize-none text-[#1E293B]"
                />
              </div>

              {status === 'error' && (
                <div className="flex items-center space-x-2 text-red-500 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  <span>{t('feedback.error_msg')}</span>
                </div>
              )}

              <div className="flex items-center justify-end space-x-4 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-3 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
                >
                  {t('feedback.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={status === 'sending' || !message.trim()}
                  className="inline-flex items-center space-x-2 px-8 py-3 bg-[#0F172A] text-white rounded-xl font-medium hover:bg-[#1E293B] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-gray-200"
                >
                  {status === 'sending' ? (
                    <span>{t('feedback.sending')}</span>
                  ) : (
                    <>
                      <span>{t('feedback.send')}</span>
                      <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedbackModal;
